import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';
import { Expense } from './Expense';
import { Income } from './Income';
import { Children } from './Children';
import { EarnedIncome } from './EarnedIncome';
import { ReplacementIncome } from './ReplacementIncome';
import { Asset } from './Asset';
import { Liability } from './Liability';
import { Customer } from '../customer/Customer';
import { Retirement401K } from './Retirement401K';
import { SSN } from './SSN';
import { HealthCare } from './HealthCare';
import { ChildCare } from './ChildCare';
import { Assumptions } from '../static/Assumptions';
import { Insurance } from './Insurance';

@supertypeClass({ toClient: true, toServer: true })
export class CapitalNeeds extends Everything {
  @property()
  flows;

  @property()
  endTime: number;

  @property()
  startTime: number;

  @property()
  savedScenario: {
    retirementAge;
    leaveTheNestAge;
    overrideAmount: number;
    overrideTerm: number;
  };

  @property()
  hasSavedNeeds: boolean = false;

  @property({ type: Array, applicationOmit: true, getType: () => Expense })
  expenses: Array<Expense> = [];

  @property({ type: Array, applicationOmit: true, getType: () => Income })
  income: Array<Income> = [];

  @property({ type: Array, getType: () => Children })
  children: Array<Children> = [];

  @property({ type: Array, applicationOmit: true, getType: () => EarnedIncome })
  earnedIncome: Array<EarnedIncome> = [];

  @property({ type: Array, applicationOmit: true, getType: () => ReplacementIncome })
  replacementIncome: Array<ReplacementIncome> = [];

  @property({ type: Array, applicationOmit: true, getType: () => Asset })
  assets: Array<Asset> = [];

  @property({ type: Array, applicationOmit: true, getType: () => Liability })
  liabilities: Array<Liability> = [];

  @property()
  discountRate: number = 0.04;

  @property({ isLocal: true })
  assetTypes = { savings: 'Savings', investments: 'Investments', Other: 'Other' };

  @property({ isLocal: true })
  liabilityTypes = {
    cards: 'Credit card',
    loan: 'Loan',
    mortgage: 'Home Mortgage',
    final: 'Final expense',
    other: 'Other',
  };

  @property({ isLocal: true, type: Array, getType: () => String })
  liabilityTypeList: Array<string> = ['cards', 'loan', 'mortgage', 'other'];

  @property({ isLocal: true })
  incomeOptionTypes = { none: 'not work', work: 'earn another amount', same: 'earn the same' };

  @property({ isLocal: true })
  expenseTypes = { health: 'Health insurance' };

  @property({ isLocal: true })
  private _haveAnyDebts: boolean;

  @property({ isLocal: true })
  get haveAnyDebts(): boolean {
    return !this.payOffMortgage && !this.payOffDebts;
  }

  set haveAnyDebts(noDebts: boolean) {
    if (noDebts) {
      this.payOffMortgage = false;
      this.payOffMortgageTrigger();
      this.payOffDebts = false;
      this.payOffDebtsTrigger();
    }
  }

  @property({ values: { false: 'No', true: 'Yes' } })
  payOffDebts: boolean;

  @property()
  payOffDebtsPercent: number = 1;

  @property({ values: { false: 'No', true: 'Yes' } })
  payOffMortgagePercent: number = 1;

  @property({ values: { false: 'No', true: 'Yes' } })
  payOffMortgage: boolean;

  @property({ values: { false: 'not paying for college', true: 'paying for college' } })
  sendKidsToCollege: boolean = true;

  @property()
  replaceIncome: boolean;

  @property()
  considerRetirement: boolean;

  @property({ isLocal: true })
  cache = {};

  @property()
  includeImputedSavings: boolean;

  @property()
  currentScenario: number = 1;

  @property()
  currentStandardScenario: number = 1;

  @property()
  scenarioDescriptions = {};

  @property()
  incomeDescription = {};

  @property()
  scenarioNPVReplacementIncome = {};

  @property({
    values: {
      '0': 'none of my salary',
      '1': 'all of my salary',
      '0.25': 'some of my salary (25%)',
      '0.5': 'half of my salary',
      '0.75': 'most of my salary (75%)',
    },
  })
  customReplacementIncome1: number = 1;

  @property({
    values: {
      '0': 'none of my salary',
      '1': 'all of my salary',
      '0.25': 'some of my salary (25%)',
      '0.5': 'half of my salary',
      '0.75': 'most of my salary (75%)',
    },
  })
  customReplacementIncome2: number = 0.5;

  @property({
    values: {
      '18': 'the kids are 18',
      '19': 'the kids are 20',
      '21': 'the kids are 21',
      '25': 'the kids are 25',
      '30': 'the kids are 30',
    },
  })
  customLeaveTheNestAge: number = 21;

  @property({
    values: function () {
      const ret = {};

      if (this.sendKidsToCollege) {
        ret[1] = '1 year of college';

        if (this.customer.assumptions.collegeCosts[this.customer.profile.collegeType].period >= 2) {
          ret[2] = '2 years of college';
        }

        if (this.customer.assumptions.collegeCosts[this.customer.profile.collegeType].period >= 3) {
          ret[3] = '3 years of college';
        }

        if (this.customer.assumptions.collegeCosts[this.customer.profile.collegeType].period >= 4) {
          ret[4] = '4 years of college';
        }
      }

      return ret;
    },
  })
  customCollegeMaxYears: number = 2;

  @property({
    values: {
      '0': 'none of my debt',
      '1': 'all of my debt',
      '0.25': 'a quarter of my debt',
      '0.5': 'half of my debt',
    },
  })
  customPayOffDebtsPercent: number = 1;

  @property({
    values: function () {
      const ret = {};

      if (this.customer.alternateCustomer.person.age < this.customRetirementAge - 20) {
        ret[this.customRetirementAge - 20] = 'my spouse is ' + (this.customRetirementAge - 20);
      }

      if (this.customer.alternateCustomer.person.age < this.customRetirementAge - 10) {
        ret[this.customRetirementAge - 10] = 'my spouse is ' + (this.customRetirementAge - 10);
      }

      if (this.customer.alternateCustomer.person.age < this.customRetirementAge) {
        ret[this.customRetirementAge] = 'my spouse is ' + this.customRetirementAge;
      }

      if (this.customer.alternateCustomer.person.age < this.customRetirementAge + 10) {
        ret[this.customRetirementAge + 10] = 'my spouse is ' + (this.customRetirementAge + 10);
      }

      return ret;
    },
  })
  customRetirementAge: number = 65;

  @property({ values: { '10': '10 years', '15': '15 years', '20': '20 years' } })
  customYearsToPayDebt: number = 20;

  @property({ isLocal: true })
  marriedWithChildrenRule: string;

  @property({ validate: 'isWithin(0, 10000000)', isLocal: true, rule: ['currency'] })
  private _existingInsurance: number;

  get existingInsurance(): number {
    return this.getExistingInsurance(false);
  }

  set existingInsurance(value: number) {
    const policyCount = this.getAssets('insurance').length;

    if (policyCount === 0) {
      this.addPolicy();
    }

    // Set the amount of the first to the given value?
    this.getAssets('insurance')[0].amount = value;
  }

  @property({ isLocal: true })
  flowYear: number;

  @property({ isLocal: true })
  biggestFlowYear: number;

  @property()
  isPrimary: boolean = true;

  @property({ getType: () => Customer })
  customer: Customer;

  @property()
  allowOrdering;

  constructor() {
    super();
  }

  static new(customer) {
    return new CapitalNeeds().init(customer);
  }

  init(customer) {
    this.customer = customer;
    this.earnedIncome[0] = EarnedIncome.new(this.customer, this.customer.primaryCustomer, 0);
    this.earnedIncome[1] = EarnedIncome.new(this.customer, this.customer.alternateCustomer, 0);
    this.replacementIncome.push(ReplacementIncome.new(this, 1, 0.8, 'Kids at home'));
    this.replacementIncome.push(ReplacementIncome.new(this, 2, 0.6, 'Kids grown'));
    this.replacementIncome.push(ReplacementIncome.new(this, 3, 1, 'Retirement'));
    this.assets.push(Retirement401K.new(this.earnedIncome[0], 0, 0));
    this.assets.push(Retirement401K.new(this.earnedIncome[1], 0, 0));

    for (const assetType in this.assetTypes) {
      this.addAsset(assetType);
    }

    this.income.push(SSN.new(this.earnedIncome[0], this.customer.primaryCustomer));
    this.income.push(SSN.new(this.earnedIncome[1], this.customer.alternateCustomer));
    this.addLiability('mortgage');
    this.addLiability('cards');
    this.addLiability('loan');
    this.addLiability('other');
    this.addLiability('final', 2e4);
    this.expenses.push(HealthCare.new(this.customer));
    this.clientInit();
    return this;
  }

  childrenTrigger() {}

  getProp(prop) {
    const descriptions = this.getBiggestFlow().descriptions;

    // if(!descriptions) return null;
    if (!descriptions) {
      return null;
    }

    let propVal = 0;

    descriptions.map(
      function (currDesc, index) {
        if (currDesc.indexOf(prop) === 0) {
          propVal += this.getBiggestFlow().npvs[index];
        }
      }.bind(this)
    );

    return propVal;
  }

  payOffDebtsTrigger() {
    if (!this.payOffDebts) {
      this.getLiabilityLoan().amount = 0;
    }
  }

  payOffMortgageTrigger() {
    if (!this.payOffMortgage) {
      this.getLiabilityMortgage().amount = 0;
    }
  }

  sendKidsToCollegeTrigger() {
    this.customer.profile.collegeType = this.sendKidsToCollege ? 'inState4yr' : 'none';
  }

  deleteAlternate() {
    this.earnedIncome[1].reset();
    this.assets[1].reset();
  }

  clientInit() {
    // New cash flow items need to be added here to account for old data that may not have them;
    if (this.getExpenses('childcare').length === 0) {
      this.expenses.push(ChildCare.new(this.customer));
    }

    this.resetOrder();
  }

  resetOrder() {
    // If this is related to a policy that has been submitted don't do this because
    // we won't be displaying or using the info and it is a violation to do so
    // However admin needs to do this to display stuff so we have an override
    // where admin sets a flag to allow this to happen since it won't save.
    if (
      !this.allowOrdering &&
      this.customer.application &&
      this.customer.application.primaryPolicy &&
      this.customer.application.primaryPolicy.workflowState
    ) {
      return;
    }

    // Hack because we now only have earned income for primary
    if (this.earnedIncome[1].amount) {
      const earnedPrimary = this.earnedIncome[1];
      const earnedAlternate = this.earnedIncome[0];

      this.earnedIncome[0] = earnedPrimary;
      this.earnedIncome[1] = earnedAlternate;
    }

    // Account for lack of guarnteed order
    if (this.income[0].earnedIncome !== this.earnedIncome[0]) {
      const primary = this.income[1];
      const alternate = this.income[0];

      this.income[0] = primary;
      this.income[1] = alternate;
    }

    this.replacementIncome.sort(function (a, b) {
      return a.title > b.title ? 1 : -1;
    });
  }

  createCopyWithCustomer(newCustomer) {
    // eslint-disable-next-line no-unused-vars
    const capitalNeeds = this.createCopy(
      function (obj, prop, template) {
        switch (template.__name__) {
          case 'Assumptions':
            return Assumptions;

          case 'Customer':
            return [newCustomer];

          // Don't traverse
          case 'HavenCustomer':
            return [newCustomer];

          // Don't traverse
          case 'CoverpathCustomer':
            return [newCustomer];

          // Don't traverse
          case 'Applicant':
            return [newCustomer.primaryCustomer];

          // Don't traverse
          case 'CapitalNeeds':
            return [newCustomer.capitalNeeds];
        }
      }.bind(this)
    );

    capitalNeeds.earnedIncome[1].applicant = newCustomer.alternateCustomer;
    capitalNeeds.income[1].applicant = newCustomer.alternateCustomer;

    return capitalNeeds;
  }

  compute() {
    this.cache = {};
    this.adjustChildren();
    this.adjustAssetsLiabilities();
  }

  getScenarioCoverage(alternateDeceased, scenario) {
    const coverage = this.getCoverage(alternateDeceased, scenario, true);

    this.adjustScenario(scenario);

    if (coverage === 0) {
      this.scenarioDescriptions[scenario] = null;
    }

    return coverage;
  }

  getCoverage(alternateDeceased, scenario?, getMaxIncome?) {
    if (typeof alternateDeceased === 'undefined') {
      alternateDeceased = this.customer.customerSelection > 0;
    }

    return this.getFace(alternateDeceased, scenario, getMaxIncome);
  }

  increaseCoverage(amount, justTesting) {
    const cover1 = this.replacementIncome[0].covered;
    const cover2 = this.replacementIncome[1].covered;

    amount = amount ? amount : 0.1;

    if (this.customer.profile.numberOfChildren > 0) {
      this.replacementIncome[0].covered =
        Math.round(Math.min(Math.max(Number(this.replacementIncome[0].covered) + amount, 0), 1.5) * 10) / 10;
    }

    if (this.customer.profile.relationship !== 'single') {
      const covered = Number(this.replacementIncome[1].covered);
      let increment;

      if (covered > 0.6) {
        increment = amount;
      } else if (covered === 0.6) {
        if (amount < 0) {
          increment = amount * 3;
        } else {
          increment = amount;
        }
      } else {
        increment = amount * 3;
      }

      this.replacementIncome[1].covered = Math.round(Math.min(Math.max(covered + increment, 0), 1.5) * 10) / 10;
    }

    if (!justTesting) {
      this.cache = {};

      if (!this.needsCoverage()) {
        this.replacementIncome[0].covered = cover1;
        this.replacementIncome[1].covered = cover2;
      }
    }

    if (justTesting) {
      const canChange = cover1 !== this.replacementIncome[0].covered || cover2 !== this.replacementIncome[1].covered;

      this.replacementIncome[0].covered = cover1;
      this.replacementIncome[1].covered = cover2;

      return canChange;
    }
  }

  decreaseCoverage(justTesting) {
    return this.increaseCoverage(-0.1, justTesting);
  }

  canIncreaseCoverage() {
    return this.increaseCoverage(null, true);
  }

  canDecreaseCoverage() {
    return this.decreaseCoverage(true);
  }

  needsCoverage() {
    return this.getCoverage(false) > 0 || this.getCoverage(true) > 0;
  }

  getGrossPay() {
    return this.earnedIncome[0].amount + this.earnedIncome[1].amount;
  }

  hasAsset(asset) {
    for (let ix = 0; ix < this.assets.length; ++ix) {
      if (this.assets[ix].subtype === asset && this.assets[ix].amount > 0) {
        return true;
      }
    }

    return false;
  }

  getAssetSavings() {
    return this.getAsset('savings');
  }

  getAssetInsurance() {
    return this.getAsset('insurance');
  }

  getExistingInsurance(isAlternateCustomer) {
    let total = 0;

    for (let ix = 0; ix < this.getAssets('insurance').length; ++ix) {
      if (this.getAssets('insurance')[ix].forAlternate === isAlternateCustomer) {
        total += this.getAssets('insurance')[ix].amount;
      }
    }

    return total;
  }

  getAsset(subtype) {
    return this.getAssets(subtype)[0];
  }

  getAssets(subtype) {
    const assets = [];

    for (let ix = 0; ix < this.assets.length; ++ix) {
      if (this.subTypeMatch(this.assets[ix].subtype, subtype)) {
        assets.push(this.assets[ix]);
      }
    }

    return assets;
  }

  subTypeMatch(a, b) {
    if (!b) {
      return true;
    }

    if (b instanceof Array) {
      for (let ix = 0; ix < b.length; ++ix) {
        if (a === b[ix]) {
          return true;
        }
      }
    } else if (a === b) {
      return true;
    }

    return false;
  }

  sumAssets(subtype) {
    const assets = this.getAssets(subtype);
    let sum = 0;

    for (let ix = 0; ix < assets.length; ++ix) {
      sum += assets[ix].amount;
    }

    return sum;
  }

  getLiabilityMortgage() {
    return this.getLiability('mortgage');
  }

  getLiabilityOther() {
    return this.getLiability('other');
  }

  getLiabilityLoan() {
    return this.getLiability('loan');
  }

  getLiabilityFinal() {
    return this.getLiability('final');
  }

  getLiability(subtype) {
    return this.getLiabilities(subtype)[0];
  }

  getLiabilities(subtype) {
    const liability = [];

    for (let ix = 0; ix < this.liabilities.length; ++ix) {
      if (!subtype || this.liabilities[ix].subtype === subtype) {
        liability.push(this.liabilities[ix]);
      }
    }

    return liability;
  }

  sumLiabilities(subtype) {
    const liabilities = this.getLiabilities(subtype);
    let sum = 0;

    for (let ix = 0; ix < liabilities.length; ++ix) {
      sum += liabilities[ix].amount;
    }

    return sum;
  }

  getDebt() {
    return <any>this.sumLiabilities - this.getLiabilityFinal().amount;
  }

  getExpenseHealthCare() {
    return this.getExpenses('healthcare')[0];
  }

  isHealthCareNeeded() {
    return (
      (this.children.length > 0 || this.customer.profile.relationship !== 'single') &&
      ((!this.earnedIncome[0].hasHealthCare && this.getCoverage(true)) ||
        (!this.earnedIncome[1].hasHealthCare && this.getCoverage(false)))
    );
  }

  getExpenseChildCare() {
    return this.getExpenses('childcare')[0];
  }

  bothCovered() {
    return this.getCoverage(false) > 0 && this.getCoverage(true) > 0;
  }

  isChildCareNeeded() {
    return (
      this.children.length > 0 &&
      ((this.earnedIncome[0].needsChildCare && this.getCoverage(true)) ||
        (this.earnedIncome[1].needsChildCare && this.getCoverage(false)))
    );
  }

  getExpenses(subtype) {
    const expenses = [];

    for (let ix = 0; ix < this.expenses.length; ++ix) {
      if (this.expenses[ix].subtype === subtype) {
        expenses.push(this.expenses[ix]);
      }
    }

    return expenses;
  }

  getLastYear(deceased) {
    // No kids and single no cash flow
    if (this.children.length === 0 && this.customer.profile.relationship === 'single') {
      return 0;
    }

    // Kids and single only kids are relavent
    if (this.children.length > 0 && this.customer.profile.relationship === 'single') {
      return Math.max(this.getFinishCollegeYear(), this.getLeaveTheNestYear());
    }

    let retirementMax = 0;

    if (this.replacementIncome[2].amount) {
      retirementMax = this.customer.settings.mortalityAge - this.customer.getSurvivor(deceased).person.age;
    } else {
      retirementMax = this.customer.settings.retirementAge - this.customer.getSurvivor(deceased).person.age;
    }

    // With kids cover until they leave the nest or until they finish college
    if (this.children.length > 0) {
      return Math.max(this.getFinishCollegeYear(), Math.max(retirementMax, this.getLeaveTheNestYear()));
    } else {
      return retirementMax;
    }
  }

  getOldestPerson() {
    return this.customer.profile.relationship === 'single'
      ? this.customer.primaryCustomer.person.age
      : Math.max(this.customer.primaryCustomer.person.age, this.customer.alternateCustomer.person.age);
  }

  getYoungestPerson() {
    return this.customer.profile.relationship === 'single'
      ? this.customer.primaryCustomer.person.age
      : Math.min(this.customer.primaryCustomer.person.age, this.customer.alternateCustomer.person.age);
  }

  getAgeOfYoungestChild() {
    let age = 99;

    for (let ix = 0; ix < this.children.length; ++ix) {
      if (this.children[ix].age < age) {
        age = this.children[ix].age;
      }
    }

    return age;
  }

  getAgeOfOldestChild() {
    let age = 0;

    for (let ix = 0; ix < this.children.length; ++ix) {
      if (this.children[ix].age > age) {
        age = this.children[ix].age;
      }
    }

    return age;
  }

  getLeaveTheNestYear() {
    return Math.max(0, this.customer.settings.leaveTheNestAge - this.getAgeOfYoungestChild());
  }

  getFinishCollegeYear() {
    const costs = this.customer.assumptions.collegeCosts[this.customer.profile.collegeType];

    return this.getStartCollegeYear() + (costs ? costs.period : 4);
  }

  getStartCollegeYear() {
    return Math.max(this.customer.assumptions.startCollege - this.getAgeOfYoungestChild(), 0);
  }

  getRetirementYear(deceased) {
    return Math.max(0, this.customer.settings.retirementAge - this.customer.getSurvivor(deceased).person.age);
  }

  getMedicareYear(deceased) {
    return Math.max(0, this.customer.assumptions.medicareYear - this.customer.getSurvivor(deceased).person.age);
  }

  addAsset(subtype) {
    this.assets.push(Asset.new(this.customer, subtype, 0));
  }

  addPolicy(customer?) {
    this.assets.push(Insurance.new(this.customer, 0, customer ? customer : this.customer.primaryCustomer));
  }

  getPolicies(customer, onlyReplacements) {
    const policies = [];
    const forAlternate = customer === this.customer.alternateCustomer;
    const allPolicies = this.getAssets('insurance');

    for (let ix = 0; ix < allPolicies.length; ++ix) {
      if (allPolicies[ix].forAlternate === forAlternate && (!onlyReplacements || allPolicies[ix].willReplace)) {
        policies.push(allPolicies[ix]);
      }
    }

    return policies;
  }

  removeAsset(asset) {
    for (let ix = 0; ix < this.assets.length; ++ix) {
      if (asset === this.assets[ix]) {
        this.assets.splice(ix, 1);
      }
    }
  }

  addLiability(subtype, amount?) {
    this.liabilities.push(Liability.new(this.customer, subtype, amount ? amount : 0, this.liabilityTypes[subtype]));
  }

  removeLiability(asset) {
    for (let ix = 0; ix < this.liabilities.length; ++ix) {
      if (asset === this.liabilities[ix]) {
        this.liabilities.splice(ix, 1);
      }
    }
  }

  addChildren(age) {
    this.children.push(Children.new(this.customer, age || 2));
  }

  ageTrigger(person) {
    // this.earnedIncome[person === this.customer.primaryCustomer ? 0 : 1].ageTrigger(person.person.age);
    this.earnedIncome[person.isPrimaryApplicant() ? 0 : 1].ageTrigger();
  }

  adjustAssetsLiabilities() {}

  adjustChildren() {
    let oldest = this.getAgeOfOldestChild() || 2;

    while (this.children.length < this.customer.profile.numberOfChildren) {
      this.addChildren(oldest++);
    }

    while (this.children.length > this.customer.profile.numberOfChildren) {
      this.children.splice(this.children.length - 1, 1);
    }
  }

  saveScenario() {
    this.savedScenario = {
      retirementAge: this.customer.settings.retirementAge,
      leaveTheNestAge: this.customer.settings.leaveTheNestAge,
      overrideAmount: this.customer.primaryCustomer.overrideAmount,
      overrideTerm: this.customer.primaryCustomer.overrideTerm,
    };
  }

  restoreScenario() {
    this.customer.settings.retirementAge = this.savedScenario.retirementAge;
    this.customer.settings.leaveTheNestAge = this.savedScenario.leaveTheNestAge;
    this.customer.primaryCustomer.overrideAmount = this.savedScenario.overrideAmount;
    this.customer.primaryCustomer.overrideTerm = this.savedScenario.overrideTerm;
  }

  hasKids() {
    return this.customer.profile.numberOfChildren > 0;
  }

  hasIncome() {
    return this.customer.capitalNeeds.earnedIncome[0].amount > 0;
  }

  hasSpouse() {
    return this.customer.profile.relationship !== 'single';
  }

  hasDebt() {
    return this.getLiabilityMortgage().amount + this.getLiabilityLoan().amount > 0;
  }

  hasTimeFrame() {
    if (!this.hasKids() && !this.hasSpouse()) {
      return false;
    }

    if (
      this.customer.capitalNeeds.earnedIncome[0].amount > 0 &&
      (this.hasKids() ? this.customReplacementIncome1 : 0) + (this.hasSpouse() ? this.customReplacementIncome2 : 0) > 0
    ) {
      return true;
    }

    if (this.hasKids() && this.sendKidsToCollege && this.customCollegeMaxYears > 0) {
      return true;
    }

    return false;
  }

  setScenario(scenario) {
    this.resetOrder();
    this.customer.primaryCustomer.overrideTerm = null;
    this.customer.primaryCustomer.overrideAmount = null;

    let overrideTerm = null;

    if (typeof scenario === 'undefined') {
      scenario = this.currentScenario;
    }

    // -------- If they have kids and spouse ---------
    //    If youngest child < 11
    //      Use single parent rules - SILVER
    //      Use single parent rules - GOLD
    //      Use single parent rules- PLAT
    // If youngest child > 10
    //   Use single parent rules - SILVER
    //   Use married no kids rules with 2 years college - GOLD
    //     Use married no kids rules with 4 years college - PLAT
    if (this.hasSpouse() && this.hasKids()) {
      // Custom
      // * Variable percent of coverage for a variable time and variable mortgage payment
      if (scenario !== 3) {
        if (this.getAgeOfYoungestChild() < 10) {
          this.singleParentSettings(scenario);

          // Remember this rule
          this.marriedWithChildrenRule = 'singleparent';
        } else {
          this.marriedNoKidsSettings(scenario);

          // Use single parent rules for calculating college years
          this.customer.profile.collegeMaxYears = this.adjustCollegeYears([0, 2, 4])[scenario];

          // Remember this rule
          this.marriedWithChildrenRule = 'marriednokids';
        }
      } else {
        this.replacementIncome[0].covered = Number(this.customReplacementIncome1);
        this.replacementIncome[1].covered = Number(this.customReplacementIncome2);
        this.replacementIncome[2].covered = 0;

        if (this.hasTimeFrame()) {
          this.customer.settings.leaveTheNestAge = Number(this.customLeaveTheNestAge);
          this.customer.settings.retirementAge = Number(this.customRetirementAge);
          this.customer.profile.collegeMaxYears = Number(this.customCollegeMaxYears);
          this.payOffDebtsPercent = Number(this.customPayOffDebtsPercent);
          this.payOffMortgagePercent = Number(this.customPayOffDebtsPercent);
        } else {
          this.payOffDebtsPercent = Number(this.customPayOffDebtsPercent);
          this.payOffMortgagePercent = Number(this.customPayOffDebtsPercent);
          this.customer.primaryCustomer.overrideTerm = Number(this.customYearsToPayDebt);
          this.customer.primaryCustomer.overrideAmount =
            this.payOffDebtsPercent * this.getLiabilityMortgage().amount +
            this.payOffMortgagePercent * this.getLiabilityLoan().amount +
            this.getLiabilityFinal().amount;
        }
      }
    } else if (!this.hasSpouse() && this.hasKids()) {
      if (scenario !== 3) {
        this.singleParentSettings(scenario);
      } else {
        this.replacementIncome[0].covered = Number(this.customReplacementIncome1);
        this.replacementIncome[1].covered = 0;
        this.replacementIncome[2].covered = 0;

        if (this.hasTimeFrame()) {
          this.customer.profile.collegeMaxYears = Number(this.customCollegeMaxYears);
          this.customer.settings.leaveTheNestAge = Number(this.customLeaveTheNestAge);
          this.customer.settings.retirementAge = Number(this.customRetirementAge);
          this.payOffDebtsPercent = Number(this.customPayOffDebtsPercent);
          this.payOffMortgagePercent = Number(this.customPayOffDebtsPercent);
        } else {
          this.payOffDebtsPercent = Number(this.customPayOffDebtsPercent);
          this.payOffMortgagePercent = Number(this.customPayOffDebtsPercent);
          this.customer.primaryCustomer.overrideTerm = Number(this.customYearsToPayDebt);
          this.customer.primaryCustomer.overrideAmount =
            this.payOffDebtsPercent * this.getLiabilityMortgage().amount +
            this.payOffMortgagePercent * this.getLiabilityLoan().amount +
            this.getLiabilityFinal().amount;
        }
      }
    } else if (this.hasSpouse() && !this.hasKids()) {
      if (scenario !== 3) {
        this.marriedNoKidsSettings(scenario);
      } else {
        this.replacementIncome[0].covered = Number(this.customReplacementIncome2);
        this.replacementIncome[1].covered = Number(this.customReplacementIncome2);
        this.replacementIncome[2].covered = 0;

        if (this.hasTimeFrame()) {
          overrideTerm = this.customRetirementAge - this.customer.alternateCustomer.person.age;

          this.customer.settings.retirementAge = Number(this.customRetirementAge);
          this.payOffDebtsPercent = Number(this.customPayOffDebtsPercent);
          this.payOffMortgagePercent = Number(this.customPayOffDebtsPercent);
        } else {
          this.payOffDebtsPercent = Number(this.customPayOffDebtsPercent);
          this.payOffMortgagePercent = Number(this.customPayOffDebtsPercent);
          this.customer.primaryCustomer.overrideTerm = Number(this.customYearsToPayDebt);
          this.customer.primaryCustomer.overrideAmount =
            this.payOffDebtsPercent * this.getLiabilityMortgage().amount +
            this.payOffMortgagePercent * this.getLiabilityLoan().amount +
            this.getLiabilityFinal().amount;
        }
      }
    } else if (!this.hasSpouse() && !this.hasKids() && (this.payOffDebts || this.payOffMortgage)) {
      this.replacementIncome[0].covered = 0;
      this.replacementIncome[1].covered = 0;
      this.replacementIncome[2].covered = 0;
      this.customer.primaryCustomer.overrideAmount =
        this.payOffDebtsPercent * this.getLiabilityMortgage().amount +
        this.payOffMortgagePercent * this.getLiabilityLoan().amount +
        this.getLiabilityFinal().amount;

      if (scenario !== 3) {
        this.customer.primaryCustomer.overrideTerm = [10, 20, 30][scenario];
        this.payOffDebtsPercent = 1;
        this.payOffMortgagePercent = 1;
      } else {
        this.replacementIncome[0].covered = 0;
        this.replacementIncome[1].covered = 0;
        this.replacementIncome[2].covered = 0;
        this.payOffDebtsPercent = this.customPayOffDebtsPercent;
        this.payOffMortgagePercent = this.customPayOffDebtsPercent;
        this.customer.primaryCustomer.overrideTerm = this.customYearsToPayDebt;
      }
    }

    overrideTerm = overrideTerm || this.customer.primaryCustomer.overrideTerm;

    if (overrideTerm === 25) {
      overrideTerm = 30;
    }

    const sameTerm = this.replacementIncome[0].covered === this.replacementIncome[1].covered;

    if (this.replacementIncome[0].covered > 0) {
      if (overrideTerm) {
        this.incomeDescription[scenario] =
          this.getFriendlyPercent(this.replacementIncome[0].covered) +
          ' your income ' +
          'for ' +
          overrideTerm +
          ' years';
      } else {
        this.incomeDescription[scenario] =
          (sameTerm
            ? ''
            : this.getFriendlyPercent(this.replacementIncome[0].covered) +
              ' your income ' +
              (this.customer.profile.numberOfChildren > 0
                ? 'for ' + (this.customer.settings.leaveTheNestAge - this.getAgeOfYoungestChild()) + ' years.'
                : '')) +
          (this.replacementIncome[1].covered > 0
            ? (this.customer.profile.numberOfChildren > 0 && !sameTerm ? ' And then ' : '') +
              this.getFriendlyPercent(this.replacementIncome[1].covered) +
              ' of your income ' +
              'for ' +
              (this.customer.settings.retirementAge - this.customer.alternateCustomer.person.age) +
              ' years'
            : '');
      }
    }

    // Generate descriptions
    this.scenarioDescriptions[scenario] = [];

    const BGH = ['baseline', 'healthy', 'generous'];

    if (this.replacementIncome[0].covered > 0) {
      this.scenarioDescriptions[scenario].push('A ' + BGH[scenario] + ' amount of income replacement');
    }

    if ((this.payOffDebts || this.payOffMortgage) && this.payOffDebtsPercent) {
      this.scenarioDescriptions[scenario].push(
        this.capitalize(
          this.getFriendlyPercent(this.payOffDebtsPercent) +
            ' your ' +
            (this.payOffMortgage ? 'mortgage' + (this.payOffDebts ? ' and ' : '') : '') +
            (this.payOffDebts ? 'debts' : '')
        )
      );
    }

    if (
      this.customer.profile.numberOfChildren > 0 &&
      this.sendKidsToCollege &&
      this.customer.profile.collegeType !== 'none' &&
      this.customer.profile.collegeMaxYears
    ) {
      if (
        this.customer.profile.collegeMaxYears >=
        this.customer.assumptions.collegeCosts[this.customer.profile.collegeType].period
      ) {
        this.scenarioDescriptions[scenario].push('All college costs');
      } else {
        this.scenarioDescriptions[scenario].push('Half of college costs');
      }
    }

    // Healthcare childcare description
    const isHealthCareNeeded =
      (this.children.length > 0 || this.customer.profile.relationship !== 'single') &&
      !this.earnedIncome[1].hasHealthCare;
    const isChildCareNeeded =
      this.children.length > 0 &&
      this.earnedIncome[1].needsChildCare &&
      this.getExpenseChildCare().youngCost > 0 &&
      this.getExpenseChildCare().youngCost > 0;

    if (isHealthCareNeeded || isChildCareNeeded) {
      this.scenarioDescriptions[scenario].push(
        (isChildCareNeeded ? 'Childcare' : '') + substituteWordings() + ' costs'
      );
    }

    function substituteWordings() {
      if (isHealthCareNeeded) {
        if (isChildCareNeeded) {
          return ' and health care';
        } else {
          return 'Health care';
        }
      } else {
        return '';
      }
    }

    // Other sundry items
    const hasSavings = this.customer.capitalNeeds.getAssetSavings().amount > 0;
    const hasInsurance = this.customer.capitalNeeds.existingInsurance > 0;
    const hasSocialSecurity =
      (this.customer.capitalNeeds.getLeaveTheNestYear() > 5 &&
        this.customer.profile.familyStatus === 'marriedwithchildren') ||
      this.customer.profile.familyStatus === 'singleparent';
    const financialSundry =
      (hasSavings || hasInsurance || hasSocialSecurity ? 'Your ' : '') +
      (hasSavings ? 'savings' : '') +
      (hasInsurance ? (hasSavings ? ', ' : '') + ' existing insurance' : '') +
      ((hasInsurance && hasSocialSecurity) || (hasSavings && hasSocialSecurity) ? ' and ' : '') +
      (hasSocialSecurity ? 'Social Security benefits' : '');

    if (financialSundry.length > 0) {
      this.scenarioDescriptions[scenario].push(financialSundry);
    }
  }

  getLeaveTheNestAge(scenario) {
    if (scenario === 3) {
      return this.customLeaveTheNestAge;
    }

    if (this.hasDebt() || this.sendKidsToCollege) {
      return [21, 25, 25][scenario];
    } else {
      return [21, 25, 30][scenario];
    }
  }

  singleParentSettings(scenario) {
    this.customer.settings.leaveTheNestAge = this.getLeaveTheNestAge(scenario);
    this.replacementIncome[0].covered = 1;
    this.replacementIncome[1].covered = 0;
    this.replacementIncome[2].covered = 0;
    this.customer.profile.collegeMaxYears = this.adjustCollegeYears([2, 2, 4])[scenario];
    this.payOffDebtsPercent = [0, 0.5, 1][scenario];
    this.payOffMortgagePercent = [0, 0.5, 1][scenario];
  }

  adjustCollegeYears(scenarios) {
    const costs = this.customer.assumptions.collegeCosts[this.customer.profile.collegeType];

    if (costs && costs.period === 2) {
      for (let ix = 0; ix < scenarios.length; ++ix) {
        scenarios[ix] = scenarios[ix] / 2;
      }
    }

    return scenarios;
  }

  marriedNoKidsSettings(scenario) {
    let yearsOfIncome = null;

    if (this.customer.alternateCustomer.person.age < 36) {
      yearsOfIncome = [20, 30, 30][scenario];

      this.replacementIncome[0].covered = [0.25, 0.25, 0.5][scenario];
      this.replacementIncome[1].covered = [0.25, 0.25, 0.5][scenario];
    } else if (this.customer.alternateCustomer.person.age < 46) {
      yearsOfIncome = [20, 20, 30][scenario];

      this.replacementIncome[0].covered = [0.25, 0.5, 0.5][scenario];
      this.replacementIncome[1].covered = [0.25, 0.5, 0.5][scenario];
    } else if (this.customer.alternateCustomer.person.age > 45 && this.customer.alternateCustomer.person.age < 56) {
      yearsOfIncome = [10, 15, 20][scenario];

      this.replacementIncome[0].covered = [0.5, 0.5, 0.5][scenario];
      this.replacementIncome[1].covered = [0.5, 0.5, 0.5][scenario];
    } else if (this.customer.alternateCustomer.person.age > 55) {
      yearsOfIncome = [10, 10, 20][scenario];

      this.replacementIncome[0].covered = [0.25, 0.5, 0.5][scenario];
      this.replacementIncome[1].covered = [0.25, 0.5, 0.5][scenario];
    }

    this.replacementIncome[2].covered = 0;
    this.customer.settings.retirementAge = Number(this.customer.alternateCustomer.person.age) + yearsOfIncome;
    this.payOffDebtsPercent = [0, 0.5, 0.5][scenario];
    this.payOffMortgagePercent = [0, 0.5, 0.5][scenario];
  }

  getFriendlyPercent(percent) {
    const friendly = {
      0.25: 'one quarter',
      0.5: 'half',
      0.75: 'most of',
      1: 'all',
    };

    return friendly[percent] || percent + '% of';
  }

  capitalize(str) {
    return str.substr(0, 1).toUpperCase() + str.substr(1);
  }

  getKey(...a) {
    let key = '';
    let sep = '';

    for (let ix = 0; ix < a.length; ++ix) {
      key += sep + JSON.stringify(a[ix]);
      sep = '-';
    }

    return key;
  }

  adjustScenario(scenario) {
    if (scenario > 0 && this.scenarioDescriptions[scenario - 1]) {
      this.scenarioDescriptions[scenario - 1][0].match(/A ([A-Za-z]+) amount/);

      const bghPrevious = RegExp.$1;

      if (this.scenarioNPVReplacementIncome[scenario] === this.scenarioNPVReplacementIncome[scenario - 1]) {
        this.scenarioDescriptions[scenario][0] = this.scenarioDescriptions[scenario][0].replace(
          /A ([A-Za-z]+) amount/,
          'A ' + bghPrevious + ' amount'
        );
      }
    }
  }

  getCashFlow(alternateDeceased, groups, suppressIncome, allowSurplus?, yearOfDeath?, override?, scenario?) {
    yearOfDeath = yearOfDeath ? yearOfDeath : 0;

    const key = this.getKey(
      'getCashFlow',
      alternateDeceased,
      groups,
      suppressIncome,
      allowSurplus,
      yearOfDeath,
      override,
      scenario,
      this.customer.primaryCustomer.overrideAmount,
      this.customYearsToPayDebt,
      this.customReplacementIncome1,
      this.customReplacementIncome2,
      this.customLeaveTheNestAge,
      this.customCollegeMaxYears,
      this.customPayOffDebtsPercent,
      this.customRetirementAge
    );

    if (this.cache[key]) {
      return this.cache[key];
    }

    this.resetOrder();

    const years = [];
    const types = [];
    const incomes = [];
    const details = {};
    const descriptions = [];
    const deceased = alternateDeceased ? this.customer.alternateCustomer : this.customer.primaryCustomer;

    this.startTime = new Date().getTime();

    // Set scenario only if face and term need to be computed.
    this.saveScenario();

    const quickQuote = this.customer.primaryCustomer.overrideAmount > 0;

    if (!this.customer.primaryCustomer.overrideAmount) {
      this.setScenario(scenario);
    }

    // Add up total liabilities
    let liabilities = 0;

    for (let liabIndex = 0; liabIndex < this.liabilities.length; ++liabIndex) {
      liabilities += this.liabilities[liabIndex].getStartingFlow().amount;
    }

    // Add up your assets
    let assets = 0;

    for (let assetIndex = 0; assetIndex < this.assets.length; ++assetIndex) {
      if (!this.assets[assetIndex].inCashFlow() && this.assets[assetIndex].isActive()) {
        assets += this.assets[assetIndex].getStartingFlow(deceased).amount;
      }
    }

    // Handle overrides
    let amount = override || this.customer.primaryCustomer.overrideAmount;

    if (amount) {
      if (yearOfDeath >= this.customer.primaryCustomer.overrideTerm) {
        amount = 0;
      }

      if (!quickQuote) {
        amount -= assets;
      }

      // Override amount should be rounded to the nearest 1K, otherwise
      // round to nearest 50k
      let insuranceNeededRounded;

      if (this.customer.primaryCustomer.overrideAmount) {
        insuranceNeededRounded = Math.round(Math.max(0, amount) / 1e3) * 1e3;
      } else {
        insuranceNeededRounded = Math.round(Math.max(0, amount) / 5e4) * 5e4;
      }

      const overrideFlow = {
        enabled: false,
        incomeReplacement: 0,
        insuranceNeeded: alternateDeceased ? 0 : amount,
        insuranceNeededRounded: alternateDeceased ? 0 : insuranceNeededRounded,
        overrideTerm: this.customer.primaryCustomer.overrideTerm,
        assets: quickQuote ? 0 : assets,
        liabilities: quickQuote ? 0 : liabilities,
        cashNeeded: 0,
      };

      this.cache[key] = overrideFlow;
      this.restoreScenario();

      return overrideFlow;
    }
    // Get each cashflow group
    const flowGroups = [
      {
        group: 'children',
        title: 'College',
        flow: this.sendKidsToCollege ? this.children : [],
      },
      {
        group: 'income',
        title: 'Salary',
        flow: this.earnedIncome,
      },
      {
        group: 'living',
        title: 'Money to live on',
        flow: [this.replacementIncome[0], this.replacementIncome[1]],
      },
      {
        group: 'retirement',
        title: 'Money for retirement',
        flow: [this.replacementIncome[2]],
      },
      {
        group: 'extraincome',
        title: 'Extra Income',
        flow: this.income,
      },
      {
        group: 'extraexpenses',
        title: 'Extra Expenses',
        flow: this.expenses,
      },
      {
        group: 'assets',
        title: '401K',
        flow: this.getAssets('401K'),
      },
    ];

    // Remove empty/inactive cashflowss
    for (let fx = 0; fx < flowGroups.length; ++fx) {
      if (flowGroups[fx].flow.length === 0) {
        flowGroups.splice(fx, 1);
      } else {
        let active = false;

        for (let jx = 0; jx < flowGroups[fx].flow.length; ++jx) {
          const curFlow = flowGroups[fx].flow[jx];

          if (curFlow.isActive(deceased) && curFlow.inCashFlow(deceased) && !(suppressIncome && curFlow.isIncome())) {
            active = true;
          }
        }

        if (!active) {
          flowGroups.splice(fx, 1);
        }
      }
    }

    // Walk through cash flow groups and accumulate amounts
    const amounts = [];
    const maxGroup = {};

    for (let gx = 0; gx < flowGroups.length; ++gx) {
      const flows = flowGroups[gx].flow;

      // Array of cash flow items
      amounts[gx] = [];

      // Walk through cash flow items
      for (let sgx = 0; sgx < flows.length; ++sgx) {
        const flow = flows[sgx].getFlow(deceased, yearOfDeath);

        amounts[gx][sgx] = flow.amounts;

        // See if we have groups for finding max value
        if (flows[sgx].maxGroup) {
          if (!maxGroup[flows[sgx].maxGroup]) {
            maxGroup[flows[sgx].maxGroup] = [];
          }

          maxGroup[flows[sgx].maxGroup].push(amounts[gx][sgx]);
        }

        if (!details[flows[sgx].type]) {
          details[flows[sgx].type] = {};
        }

        if (!details[flows[sgx].type][flows[sgx].subtype]) {
          details[flows[sgx].type][flows[sgx].subtype] = [];
        }

        details[flows[sgx].type][flows[sgx].subtype].push(flow.details);
      }
    }

    // Process any groups where you have to take the maximum within the group
    for (const groupKey in maxGroup) {
      const groupItems = maxGroup[groupKey];

      // Go through each year
      for (let innerx = 0; innerx < groupItems[0].length; ++innerx) {
        // Find maximum value of any item
        let max = 0;

        for (let gix = 0; gix < groupItems.length; ++gix) {
          max = Math.max(groupItems[gix][innerx], max);
        }

        // Kill anything lower than the max
        for (let gix = 0; gix < groupItems.length; ++gix) {
          groupItems[gix][innerx] = groupItems[gix][innerx] < max ? 0 : groupItems[gix][innerx];
        }
      }
    }

    // Walk through cashflow groups
    for (let yix = 0; yix < this.getLastYear(deceased); ++yix) {
      // Walk through cash flow groups
      let ix = 0;
      const items = [];

      // Cash flow item amounts
      for (let gx = 0; gx < flowGroups.length; ++gx) {
        const flows = flowGroups[gx].flow;

        // Array of cash flow items
        const groupItems = groups && groups.match(flowGroups[gx].group);

        if (groupItems) {
          items[ix] = 0;
        }

        // Zero total if grouping
        let groupFound = false;

        // Walk through cash flow items
        for (let flowx = 0; flowx < flows.length; ++flowx) {
          const flow = flows[flowx];

          // cash flow item
          // Total by group or just add to result
          if (flow.isActive(deceased) && flow.inCashFlow(deceased) && !(suppressIncome && flow.isIncome())) {
            if (groupItems) {
              types[ix] = flow.type;
              incomes[ix] = flow.inCashFlow();
              items[ix] += amounts[gx][flowx][yix];
              descriptions[ix] = flowGroups[gx].title;
              groupFound = true;
            } else {
              types[ix] = flow.type;
              items[ix] = amounts[gx][flowx][yix];
              incomes[ix] = flow.inCashFlow();
              descriptions[ix] = flow.getDescription(deceased);
              ++ix;
            }
          }
        }

        if (groupItems && groupFound) {
          ++ix;
        }
      }

      let deficit = 0;

      for (let dix = 0; dix < items.length; ++dix) {
        deficit += items[dix];
      }

      if (Assumptions.allowSurplus) {
        years[yix] = {
          items: items,
          deficit: deficit,
          year: yix,
        };
      } else {
        years[yix] = {
          items: items,
          deficit: Math.min(0, deficit),
          year: yix,
        };
      }
    }

    // Get imputed savings
    const earnedIncome = this.earnedIncome[alternateDeceased ? 1 : 0];
    let savings = 0;

    // Go back in time using salary increase rate
    let earnings = earnedIncome.amount;
    let currentYear = new Date().getFullYear();
    const firstYearWorking = currentYear - earnedIncome.yearsWorked + yearOfDeath;

    while (currentYear-- >= firstYearWorking) {
      earnings = earnings - earnings * earnedIncome.increase;
    }

    // Go forward to the year of death
    if (this.includeImputedSavings) {
      earnings = earnedIncome.amount;

      for (; currentYear < new Date().getFullYear() + yearOfDeath; ++currentYear) {
        earnings = earnings + earnings * earnedIncome.increase;
        savings += earnings * this.customer.assumptions.savingsRate;
        savings += savings * this.customer.settings.discountRate;
      }
    }

    // Calculate NPVs
    const npvs = [];

    for (let nix = 0; nix <= types.length; ++nix) {
      npvs[nix] = 0;
    }

    for (let yix = yearOfDeath; yix < this.getLastYear(deceased); ++yix) {
      const yearsPassed = yix - yearOfDeath;

      let nix = 0;
      for (; nix < types.length; ++nix) {
        npvs[nix] += years[yix].items[nix] / Math.pow(1 + this.customer.settings.discountRate, yearsPassed + 1);
      }

      npvs[nix] += years[yix].deficit / Math.pow(1 + this.customer.settings.discountRate, yearsPassed + 1);
    }

    let need = 0;

    for (let ix = 0; ix < descriptions.length; ++ix) {
      if (descriptions[ix].match(/need/i)) {
        need = npvs[ix] * -1;
      }
    }

    this.endTime = new Date().getTime();
    this.flows = this.flows ? this.flows + 1 : 1;

    // Start with NPV of income needs
    const cashPosition = npvs[npvs.length - 1];
    let adjustedCashPosition = cashPosition;

    adjustedCashPosition -= liabilities;
    adjustedCashPosition += assets;

    if (this.includeImputedSavings) {
      adjustedCashPosition += savings;
    }

    const flow = {
      enabled: true,
      incomeReplacement: need,
      types: types,
      years: years,
      npvs: npvs,
      isActive: incomes,
      descriptions: descriptions,
      amounts: amounts,
      insuranceNeeded: deceased.policyHidden ? 0 : Math.max(0, 0 - adjustedCashPosition),
      insuranceNeededRounded: deceased.policyHidden ? 0 : Math.round(Math.max(0, 0 - adjustedCashPosition) / 5e4) * 5e4,
      cashNeeded: cashPosition,
      assets: assets + (this.includeImputedSavings ? savings : 0),
      liabilities: liabilities,
      details: details,
    };

    this.cache[key] = flow;
    this.restoreScenario();

    return flow;
  }

  getFace(isAlternate?, scenario?, setMaxIncomeReplacement?) {
    let face = 0;
    let replacement = 0;

    for (let ix = 0; ix < this.customer.assumptions.maxTermLength; ++ix) {
      const cashFlow = this.getCashFlow(isAlternate, null, null, null, ix, null, scenario);

      if (face < cashFlow.insuranceNeededRounded) {
        face = cashFlow.insuranceNeededRounded;
        replacement = cashFlow.incomeReplacement;
      }
    }

    if (setMaxIncomeReplacement) {
      this.scenarioNPVReplacementIncome[scenario] = replacement;
    }

    return face;
  }

  getOverTime(interval, face, scenario?) {
    if (this.cache['getOverTime' + interval + face]) {
      return this.cache['getOverTime' + interval];
    }

    const series = [];

    for (let ix = 0; ix < 2; ++ix) {
      series[ix] = {
        label: ix === 0 ? this.customer.primaryCustomer.firstName : this.customer.alternateCustomer.firstName,
        data: [],
      };

      for (let jx = 0; jx < 31; jx += interval ? interval : 1) {
        series[ix].data[jx] = this.getCashFlow(
          <any>!ix === 0,
          null,
          null,
          null,
          jx,
          face,
          scenario
        ).insuranceNeededRounded;
      }
    }

    this.cache['getOverTime' + interval] = series;

    return series;
  }

  getTerm(alternateDeceased, scenario?) {
    if (typeof alternateDeceased === 'undefined') {
      alternateDeceased = this.customer.customerSelection > 0;
    }

    if (this.cache['getTerm' + alternateDeceased + scenario]) {
      return this.cache['getTerm' + alternateDeceased + scenario];
    }

    const terms = [];
    let maxCost = 0;

    // Find the amount needed in future years
    let cx = 0;

    for (let jx = 0; jx < this.customer.assumptions.maxTermLength; jx += 5) {
      let cost = 0;

      for (let kx = jx; kx < jx + 5; ++kx) {
        const flow = this.getCashFlow(alternateDeceased, null, null, null, kx, null, scenario);

        if (flow.overrideTerm) {
          return flow.overrideTerm;
        }

        cost = Math.max(cost, flow.insuranceNeeded - flow.liabilities);
      }

      terms[cx++] = {
        term: jx + 5,
        amount: cost,
      };

      if (cost > maxCost) {
        maxCost = cost;
      }
    }

    if (maxCost === 0) {
      return 0;
    }

    let term = 5;

    for (let ix = 0; ix < terms.length; ++ix) {
      if (terms[ix].amount > 0 && terms[ix].term > term) {
        term = terms[ix].term;
      }
    }

    // From needs spec - If 5 year recommended and any amount $100k or greater round to 10 years.
    // 5/27/2015 - If 5 year recommended round to 10 years period.
    if (term === 5) {
      term = 10;
    }

    if (term === 25) {
      term = 30;
    }

    this.cache['getTerm' + alternateDeceased + scenario] = term;

    return term;
  }

  getPercents() {
    const result = [];
    const cashflows = [
      this.getCashFlow(true, 'children|living|retirement', true),
      this.getCashFlow(false, 'children|living|retirement', true),
    ];
    let total = 0;
    const totals = {};
    const liabilityGroups: any = {};

    // Start with debt
    for (let ix = 0; ix < this.liabilities.length; ++ix) {
      const liability: any = this.liabilities[ix];

      total += liability.amount;
      liabilityGroups[liability.subtype] = liabilityGroups[liability.subtype]
        ? liabilityGroups[liability.subtype] + liability.amount
        : liability.amount;
    }

    // Total up other nvps and catagorize them
    for (let cx = 0; cx < 2; ++cx) {
      const cashflow = cashflows[cx];

      // Get total combined coverage
      for (let ix = 0; ix < cashflow.descriptions.length; ++ix) {
        total += Math.abs(cashflow.npvs[ix]);

        // Slot in various metrics
        const cashflowMetric = cashflow.descriptions[ix];

        totals[cashflowMetric] = totals[cashflowMetric]
          ? totals[cashflowMetric] + cashflow.npvs[ix]
          : cashflow.npvs[ix];
      }
    }

    // Get percents for each metric of total
    for (const metric in totals) {
      result.push({
        label: metric,
        amount: totals[metric],
        percent: (Math.abs(totals[metric]) * 100) / total,
      });
    }

    // Sum up the liabilities
    let debt = 0;

    for (const liability in liabilityGroups) {
      if (liabilityGroups[liability] > 0) {
        debt += Math.abs(liabilityGroups[liability]);
      }
    }

    if (debt > 0) {
      result.push({
        label: 'Pay Debts',
        amount: debt,
        percent: (100 * debt) / total,
      });
    }

    return result;
  }

  getFlow() {
    return this.isPrimary
      ? this.getCashFlow(false, null, null, null, this.flowYear)
      : this.getCashFlow(true, null, null, null, this.flowYear);
  }

  getBiggestFlow() {
    const face = this.getFace(!this.isPrimary);

    for (let ix = 0; ix < this.customer.assumptions.maxTermLength; ++ix) {
      const flow = this.getCashFlow(!this.isPrimary, null, null, null, ix);

      if (face === flow.insuranceNeededRounded) {
        this.biggestFlowYear = ix;

        return flow;
      }
    }

    return [];
  }
}
